"use client";

import { useState } from "react";
import { gtmDataToEcommerceItem } from "@/hooks/use-gtm-product-detail-tracking";
import { Button } from "@/scott/ui/elements/button/button";
import { startAddToCartFlow } from "@/services/datadog/cart-flow-telemetry";
import type { Dictionary } from "@/utils/dictionaries/get-dictionary";
import { addToCartToDataLayer } from "@/utils/gtm.utils";
import {
  type AddToAcceleratorCartResult,
  addToAcceleratorCart,
} from "../accelerator-cart";
import type {
  ProductDetailGtmData,
  ProductDetailStockStatus,
} from "../product-detail-page.adapter";

interface ProductAddToCartButtonProps {
  buttonLabel: Dictionary["productDetailPage"]["addToCart"];
  country: string;
  fallbackErrorMessage: Dictionary["productDetailPage"]["errorLoadingProduct"];
  language: string;
  productCode: string;
  /** GA4 data for the currently selected article, for the add_to_cart event. */
  gtm: ProductDetailGtmData;
  stockStatus?: ProductDetailStockStatus;
  successMessage: Dictionary["productDetailPage"]["addToCartSuccess"];
  unconfirmedErrorMessage: Dictionary["productDetailPage"]["cartUpdateUnconfirmed"];
}

interface ProductAddToCartButtonState {
  isLoading: boolean;
  errorMessage?: string;
  successMessage?: string;
}

function isAddToCartDisabled(stockStatus?: ProductDetailStockStatus): boolean {
  return stockStatus === "outOfStock";
}

function errorMessageKind(
  result: Exclude<AddToAcceleratorCartResult, { status: "success" }>,
  fallbackErrorMessage: string
): "unconfirmed" | "accelerator" | "fallback" {
  if (result.status === "unknown") {
    return "unconfirmed";
  }

  return result.errorMessage && result.errorMessage !== fallbackErrorMessage
    ? "accelerator"
    : "fallback";
}

export function ProductAddToCartButton(
  props: Readonly<ProductAddToCartButtonProps>
) {
  const [state, setState] = useState<ProductAddToCartButtonState>({
    isLoading: false,
  });

  async function handleAddToCartClick() {
    if (state.isLoading || isAddToCartDisabled(props.stockStatus)) {
      return;
    }

    setState({ isLoading: true });

    const flow = startAddToCartFlow({
      country: props.country,
      language: props.language,
      productCode: props.productCode,
      quantity: 1,
      stockStatus: props.stockStatus,
    });
    let result: AddToAcceleratorCartResult;

    try {
      result = await addToAcceleratorCart(
        {
          country: props.country,
          language: props.language,
          productCode: props.productCode,
          quantity: 1,
        },
        props.fallbackErrorMessage,
        { flow }
      );
    } catch (error) {
      // addToAcceleratorCart handles its own failures, so this is unexpected.
      // finish() is a no-op if the flow already ended.
      flow.finish({
        outcome: "error",
        reason: "unexpected_error",
        phase: "ui",
        postAttempted: false,
        outcomeKnown: false,
        errorName: error instanceof Error ? error.name : typeof error,
        error,
      });
      flow.step(
        "ui_error_shown",
        { messageKind: "fallback" },
        { level: "warn" }
      );
      setState({
        isLoading: false,
        errorMessage: props.fallbackErrorMessage,
      });
      return;
    }

    if (result.status !== "success") {
      flow.step(
        "ui_error_shown",
        { messageKind: errorMessageKind(result, props.fallbackErrorMessage) },
        { level: "warn" }
      );
      setState({
        isLoading: false,
        errorMessage:
          result.status === "unknown"
            ? props.unconfirmedErrorMessage
            : result.errorMessage || props.fallbackErrorMessage,
        successMessage: undefined,
      });
      return;
    }

    // Only a confirmed cart update reports add_to_cart, with the variant and
    // the one unit this action actually added.
    addToCartToDataLayer(gtmDataToEcommerceItem(props.gtm, { quantity: 1 }));

    setState({
      isLoading: false,
      errorMessage: undefined,
      successMessage: props.successMessage,
    });
  }

  return (
    <div>
      <Button
        className="h-auto w-full gap-2.5 px-3 py-4 font-brand font-normal text-[12px] leading-4.25 tracking-normal"
        disabled={isAddToCartDisabled(props.stockStatus)}
        id="addToCartButton"
        loading={state.isLoading}
        onClick={handleAddToCartClick}
        type="button"
      >
        {props.buttonLabel}
      </Button>
      {state.errorMessage && (
        <p aria-live="polite" className="pt-2 text-destructive text-xs">
          {state.errorMessage}
        </p>
      )}
      {state.successMessage && (
        <p aria-live="polite" className="pt-2 text-green-600 text-xs">
          {state.successMessage}
        </p>
      )}
    </div>
  );
}
